import type {
  ClientMessage,
  JoinErrorCode,
  MeetingPublic,
  Participant,
  RtcCredentials,
  ServerMessage,
} from '@shared/protocol';

export interface ChatLine {
  id: string;
  from: string;
  name: string;
  text: string;
  at: string;
}

export type ConnectionState = 'idle' | 'connecting' | 'joined' | 'reconnecting' | 'closed';

export interface SignalHandlers {
  onState: (state: ConnectionState) => void;
  onJoined: (
    self: Participant,
    meeting: MeetingPublic,
    peers: Participant[],
    rtc: RtcCredentials | null,
  ) => void;
  onPeerJoined: (participant: Participant) => void;
  onPeerLeft: (id: string) => void;
  onPeerState: (id: string, state: Pick<Participant, 'audioEnabled' | 'videoEnabled' | 'screenSharing'>) => void;
  onChat: (line: ChatLine) => void;
  onError: (code: JoinErrorCode, message: string) => void;
}

interface JoinOptions {
  code: string;
  name: string;
  audioEnabled: boolean;
  videoEnabled: boolean;
}

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15_000;
const KEEPALIVE_MS = 25_000;

/**
 * Wraps the meeting WebSocket: presence, roster and chat.
 *
 * It carries no WebRTC negotiation — the SFU has its own control channel, and
 * media credentials arrive once, on the `joined` message.
 */
export class SignalClient {
  private socket: WebSocket | null = null;
  private keepalive: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private closedByUs = false;
  private joinOptions: JoinOptions | null = null;

  constructor(private readonly handlers: SignalHandlers) {}

  join(options: JoinOptions): void {
    this.joinOptions = options;
    this.closedByUs = false;
    this.attempts = 0;
    this.open();
  }

  private open(): void {
    const options = this.joinOptions;
    if (!options) return;

    this.handlers.onState(this.attempts === 0 ? 'connecting' : 'reconnecting');

    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(`${scheme}://${location.host}/ws`);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempts = 0;
      this.send({ t: 'join', ...options });
      this.keepalive = setInterval(() => this.send({ t: 'ping' }), KEEPALIVE_MS);
    });

    socket.addEventListener('message', (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      this.dispatch(msg);
    });

    socket.addEventListener('close', () => {
      this.clearKeepalive();
      if (this.closedByUs) {
        this.handlers.onState('closed');
        return;
      }
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => socket.close());
  }

  private dispatch(msg: ServerMessage): void {
    switch (msg.t) {
      case 'joined':
        this.handlers.onState('joined');
        this.handlers.onJoined(msg.self, msg.meeting, msg.participants, msg.rtc);
        break;
      case 'peer-joined':
        this.handlers.onPeerJoined(msg.participant);
        break;
      case 'peer-left':
        this.handlers.onPeerLeft(msg.id);
        break;
      case 'peer-state':
        this.handlers.onPeerState(msg.id, {
          audioEnabled: msg.audioEnabled,
          videoEnabled: msg.videoEnabled,
          screenSharing: msg.screenSharing,
        });
        break;
      case 'chat':
        this.handlers.onChat({
          id: `${msg.from}-${msg.at}-${Math.random().toString(36).slice(2, 8)}`,
          from: msg.from,
          name: msg.name,
          text: msg.text,
          at: msg.at,
        });
        break;
      case 'error':
        // A rejected join is terminal — retrying a bad or expired code would
        // just burn through the join rate limit.
        if (msg.code !== 'server-error') this.closedByUs = true;
        this.handlers.onError(msg.code, msg.message);
        break;
      case 'pong':
        break;
    }
  }

  private scheduleReconnect(): void {
    this.attempts++;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** (this.attempts - 1), RECONNECT_MAX_MS);
    // Jitter so several participants dropped by the same restart do not all
    // come back in lockstep.
    const jittered = delay * (0.7 + Math.random() * 0.6);
    this.handlers.onState('reconnecting');
    this.reconnectTimer = setTimeout(() => this.open(), jittered);
  }

  private clearKeepalive(): void {
    if (this.keepalive) clearInterval(this.keepalive);
    this.keepalive = null;
  }

  setLocalState(state: { audioEnabled?: boolean; videoEnabled?: boolean; screenSharing?: boolean }): void {
    this.send({ t: 'state', ...state });
  }

  sendChat(text: string): void {
    this.send({ t: 'chat', text });
  }

  leave(): void {
    this.closedByUs = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.clearKeepalive();
    this.send({ t: 'leave' });
    this.socket?.close(1000, 'left');
    this.socket = null;
    this.handlers.onState('closed');
  }

  private send(msg: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(msg));
  }
}
